'use client';

import React, { CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import { CustomButton } from '@/app/appcharge/CustomButton';
import { Card } from '@/components/ui/card';
import { useEventBatchContext } from '@/hooks/useEventBatch';
import { observerElement } from '@/hooks/useObserver';
import { useStoreRedirect } from '@/hooks/useStoreRedirect';
import { EEventsType, ELocalStorageKeys } from '@/lib/enums';
import { IDailyBonusItem } from '@/lib/types';
import { cn } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';
import type { ProductData } from '@appcharge/shared-ui';

import { useTranslations } from 'next-intl';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { useDailyBonusTimer } from '@/hooks/useDailyBonusTimer';
import { webStorageUtil } from '@/state/webStorage.state.service';

interface IDailyBonusItemProps extends IDailyBonusItem {
  uniqueIdentifier: string;
  className?: string;
  backgroundImage?: string;
  backgroundImageMobile?: string;
  isStoreRedirect?: boolean;
  redirectUrl?: string;
  itemIndex: number;
  onBlockClick?: () => void;
}

const DailyBonusItemComponent: React.FC<IDailyBonusItemProps> = ({
  type,
  className,
  backgroundImage,
  backgroundImageMobile,
  redirectUrl,
  uniqueIdentifier,
  offerId,
  itemIndex,
  productsSequence,
  indexToCollect,
  isAllowedToCollect,
  onBlockClick,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const { initialBoot } = useAppState();
  const { redirect } = useStoreRedirect();
  const { timeLeftLabel } = useDailyBonusTimer();

  const currentProduct = useMemo(() => {
    const currentProductIndex = indexToCollect ? indexToCollect - 1 : 0;

    return productsSequence?.[currentProductIndex]?.products?.[0];
  }, [indexToCollect, productsSequence]);

  const isMobile = useIsMobileDevice();
  const backgroundImageUrl = isMobile && backgroundImageMobile ? backgroundImageMobile : backgroundImage;

  const cardStyle: CSSProperties = {
    backgroundColor: initialBoot?.theme?.contentCard?.backgroundColor?.colorOne ?? 'rgba(0, 0, 0, 0.25)',
  };

  const contentStyle: CSSProperties = {
    ...(backgroundImageUrl && {
      backgroundImage: `url(${backgroundImageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }),
  };

  const buttonBgColor = initialBoot?.theme?.contentCard?.buttonColor?.colorOne ?? '#000';

  const handleCollectClick = useCallback(async () => {
    try {
      onBlockClick?.();

      if (redirectUrl) {
        await redirect(redirectUrl, true, {
          autoCollectOfferId: isAllowedToCollect ? offerId : undefined,
        });
      } else {
        if (isAllowedToCollect) {
          webStorageUtil.set(ELocalStorageKeys.AUTO_COLLECT_FLAG, 'true');
        }

        const storeUrl = isAllowedToCollect ? `/store?auto_collect=true&popup_id=${offerId}` : '/store';
        router.push(storeUrl);
      }
    } catch (error) {
      console.error('Error during collect:', error);
    }
  }, [redirectUrl, router, isAllowedToCollect, redirect, offerId, onBlockClick]);

  const itemRef = useRef<HTMLDivElement>(null);
  const { sendEvent, setEventType } = useEventBatchContext();

  useEffect(() => {
    if (!itemRef?.current || !sendEvent) return;

    setEventType(EEventsType.PORTAL_CONTENT_DISPLAYED);

    observerElement(
      itemRef.current,
      {
        offerId: offerId,
        type,
        index: itemIndex,
      },
      sendEvent,
      uniqueIdentifier
    );
  }, [itemRef?.current, uniqueIdentifier, type, offerId, itemIndex]);

  if (!currentProduct) return null;

  return (
    <Card
      ref={itemRef}
      style={cardStyle}
      className={cn(
        'postItem flex w-full flex-col gap-y-2.5 rounded-[20px] border-none p-5 cursor-pointer',
        className
      )}
      onClick={handleCollectClick}>
      <div style={contentStyle} className="flex flex-col items-center gap-2 rounded-lg py-[65px]">
        {currentProduct?.images?.product && (
          <div className="flex max-h-[160px] items-center justify-center rounded-lg">
            <img
              src={currentProduct?.images?.product}
              alt="prefix"
              className="max-h-[160px] rounded object-cover"
            />
          </div>
        )}

        {currentProduct?.quantityDisplay && (
          <p className="text-2xl font-semibold" style={{ color: initialBoot?.theme?.contentCard?.textColor }}>
            {currentProduct.quantityDisplay}
          </p>
        )}
      </div>

      {timeLeftLabel && !isAllowedToCollect? (
        <CustomButton
          className="h-12 w-full text-xl font-black"
          style={{
            background: 'linear-gradient(to bottom, #CACACA, #818181)',
            border: '1px solid #E2C99B',
            color: 'white',
          }}
          icon={'https://media.appcharge.com/defaults/lock-icon.png'}
          onClick={handleCollectClick}>
          {timeLeftLabel}
        </CustomButton>
      ) : (
        <CustomButton
          className="h-12 w-full text-xl font-black"
          style={{ background: buttonBgColor }}
          onClick={handleCollectClick}>
          {t('portal.collect')}
        </CustomButton>
      )}
    </Card>
  );
};

export const DailyBonusItem = memo(DailyBonusItemComponent);
